import type { ComponentType, SVGProps } from "react";
import clsx from "clsx";

export interface DetailTabOption<T extends string> {
  value: T;
  label: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  /** Optionale Anzahl (z. B. Ansprechpartner) rechts neben dem Label. */
  count?: number | null;
}

interface DetailTabsProps<T extends string> {
  value: T;
  onChange: (next: T) => void;
  options: DetailTabOption<T>[];
  ariaLabel?: string;
  className?: string;
}

/**
 * Horizontale Tab-Leiste mit Icons und optionalen Zählern. Auf schmalen
 * Bildschirmen scrollbar, damit auch viele Bereiche ohne Abkürzungen Platz finden.
 */
export function DetailTabs<T extends string>({
  value,
  onChange,
  options,
  ariaLabel,
  className,
}: DetailTabsProps<T>) {
  return (
    <div
      className={clsx(
        "-mx-1 overflow-x-auto px-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
        className,
      )}
    >
      <div
        role="tablist"
        aria-label={ariaLabel}
        className="flex min-w-max gap-1 border-b border-separator/70"
      >
        {options.map((opt) => {
          const active = opt.value === value;
          const Icon = opt.icon;
          return (
            <button
              key={opt.value}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => onChange(opt.value)}
              className={clsx(
                "-mb-px inline-flex items-center gap-2 whitespace-nowrap border-b-2 px-3 py-2.5 text-sm font-medium transition",
                "focus:outline-none focus-visible:rounded-md focus-visible:ring-2 focus-visible:ring-accent/40",
                active
                  ? "border-accent text-label"
                  : "border-transparent text-label-secondary hover:border-separator hover:text-label",
              )}
            >
              <Icon
                className={clsx("h-4 w-4 shrink-0", active ? "text-accent" : "text-label-tertiary")}
                aria-hidden
              />
              <span>{opt.label}</span>
              {opt.count != null ? (
                <span
                  className={clsx(
                    "rounded-full px-1.5 py-0.5 text-[11px] font-semibold tabular-nums leading-none",
                    active ? "bg-accent-soft text-accent" : "bg-fill-tertiary text-label-secondary",
                  )}
                >
                  {opt.count.toLocaleString("de-DE")}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}
